import type { ChapterNumberingOverride } from "../chapterNumbering";
import { open } from "@tauri-apps/plugin-dialog";
import { openDialogDefaults } from "../dialogDefaults";
import { resolveHomeDir } from "./dialogHome";
import { invoke, tauriOnlyInvoke } from "../invoke";
import { isCloudRoot, isDemoRoot } from "../platform";
import { clearCloudEditorActiveBuffer } from "../platform/cloudEditorSession";
import { isDemoSessionActive } from "../demo/session";
import { normalizeZwConfig, type ZwConfig } from "../zwConfig";
import {
  isMobileShell,
  isTauri,
  normalizePayload,
  type EditorActiveTab,
  type EditorPayload,
  type RawEditorPayload,
} from "./core";

export type RecentProject = {
  path: string;
  name: string;
  kind: "project";
};

function normalizeRecents(
  raw: { path: string; name: string; kind?: string }[],
): RecentProject[] {
  return raw
    .filter((entry) => entry.kind === "project")
    .map((entry) => ({
      path: entry.path,
      name: entry.name,
      kind: "project" as const,
    }));
}

export async function reloadEditorFromDisk(
  rootPath: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("reload_editor_from_disk", {
    rootPath,
  });
  return normalizePayload(raw);
}

/** Create a project folder under parentPath and open it. Desktop only —
 * `zw-online` has no local-disk project registry of its own. */
export async function createNewProject(
  name: string,
  parentPath: string,
  replaceSplash = false,
): Promise<void> {
  await tauriOnlyInvoke("create_new_project", {
    name,
    parentPath,
    replaceSplash,
  });
}

/** App-private library root (`app_data_dir/projects`). Desktop only. */
export async function getProjectsRoot(): Promise<string> {
  return tauriOnlyInvoke<string>("get_projects_root");
}

/** Projects under the app-private library (mobile library UI). Desktop only. */
export async function listLocalProjects(): Promise<RecentProject[]> {
  const raw = await tauriOnlyInvoke<
    { path: string; name: string; kind?: string }[]
  >("list_local_projects");
  return normalizeRecents(raw);
}

/**
 * Parent folder for a new / restored project.
 * Mobile: app-private library (no folder picker).
 * Desktop: native directory dialog.
 */
export async function pickProjectParentFolder(
  title = "Choose where to create the project",
): Promise<string | null> {
  if (isTauri() === false) return null;
  if (await isMobileShell()) {
    return getProjectsRoot();
  }
  const home = await resolveHomeDir();
  const path = await open({
    ...openDialogDefaults(home),
    title,
    directory: true,
    multiple: false,
  });
  if (path === null || path === "" || Array.isArray(path)) return null;
  return path;
}

export async function openFolderProject(replaceSplash = false): Promise<void> {
  await tauriOnlyInvoke("open_folder_project", { replaceSplash });
}

export async function openProjectPath(
  path: string,
  replaceSplash = false,
): Promise<void> {
  await tauriOnlyInvoke("open_project_path", { path, replaceSplash });
}

export async function openPath(
  path: string,
  replaceSplash = false,
): Promise<void> {
  await tauriOnlyInvoke("open_path", { path, replaceSplash });
}

export async function getRecentProjects(): Promise<RecentProject[]> {
  if (isTauri() === false) return [];
  const raw = await tauriOnlyInvoke<
    { path: string; name: string; kind?: string }[]
  >("get_recent_projects");
  return normalizeRecents(raw);
}

export async function removeRecentProject(
  path: string,
): Promise<RecentProject[]> {
  if (isTauri() === false) return [];
  const raw = await tauriOnlyInvoke<
    { path: string; name: string; kind?: string }[]
  >("remove_recent_project", { path });
  return normalizeRecents(raw);
}

/** Delete a project folder from the app-private library (mobile). */
export async function deleteLocalProject(
  path: string,
): Promise<RecentProject[]> {
  if (isTauri() === false) return [];
  const raw = await tauriOnlyInvoke<
    { path: string; name: string; kind?: string }[]
  >("delete_local_project", { path });
  return normalizeRecents(raw);
}

export async function readZwConfig(rootPath: string): Promise<ZwConfig> {
  if (isCloudRoot(rootPath)) {
    // `read_zw_config` isn't ported to `zw-online` yet — reuse the payload's
    // embedded `project` field instead of a dedicated config fetch.
    const payload = await invoke<RawEditorPayload>("get_editor_payload");
    return normalizeZwConfig(payload.project ?? {});
  }
  const raw = await invoke<unknown>("read_zw_config", { rootPath });
  return normalizeZwConfig(raw);
}

export async function writeZwConfig(
  rootPath: string,
  config: ZwConfig,
): Promise<ZwConfig> {
  const plain = JSON.parse(JSON.stringify(config)) as ZwConfig;
  if (isCloudRoot(rootPath)) {
    throw new Error("Project settings are not editable in the web editor yet");
  }
  const raw = await invoke<unknown>("write_zw_config", {
    rootPath,
    config: plain,
  });
  return normalizeZwConfig(raw);
}

export async function createProjectChapter(
  rootPath: string,
  name: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("create_project_chapter", {
    rootPath,
    name,
  });
  return normalizePayload(raw);
}

export async function renameProjectChapter(
  rootPath: string,
  fromFolder: string,
  toFolder: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("rename_project_chapter", {
    rootPath,
    fromFolder,
    toFolder,
  });
  return normalizePayload(raw);
}

export async function deleteProjectChapter(
  rootPath: string,
  folder: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("delete_project_chapter", {
    rootPath,
    folder,
  });
  return normalizePayload(raw);
}

/** Creates an act (#91), inserted after `afterFolder`'s manuscript span or
 * appended when that's null. Never changes the open file: acts have no prose. */
export async function createProjectAct(
  rootPath: string,
  name: string,
  afterFolder: string | null = null,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("create_project_act", {
    rootPath,
    name,
    afterFolder,
  });
  return normalizePayload(raw);
}

export async function renameProjectAct(
  rootPath: string,
  fromFolder: string,
  toFolder: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("rename_project_act", {
    rootPath,
    fromFolder,
    toFolder,
  });
  return normalizePayload(raw);
}

/** Deletes the act folder only. Its chapters are promoted to top level at the
 * act's former position, never deleted along with the grouping. */
export async function deleteProjectAct(
  rootPath: string,
  folder: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("delete_project_act", {
    rootPath,
    folder,
  });
  return normalizePayload(raw);
}

export async function updateChapterSynopsis(
  rootPath: string,
  folder: string,
  synopsis: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("update_chapter_synopsis", {
    rootPath,
    folder,
    synopsis,
  });
  return normalizePayload(raw);
}

export async function updateChapterNumbering(
  rootPath: string,
  folder: string,
  numbering: ChapterNumberingOverride,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("update_chapter_numbering", {
    rootPath,
    folder,
    numbering,
  });
  return normalizePayload(raw);
}

export async function setActiveTab(
  rootPath: string,
  tab: EditorActiveTab | null,
): Promise<EditorPayload> {
  // Demo persists tab in the in-memory session. Cloud/online trusts the URL.
  if (isDemoRoot(rootPath)) {
    const raw = await invoke<RawEditorPayload>("set_active_tab", {
      rootPath,
      tab,
    });
    return normalizePayload(raw);
  }
  if (isTauri() === false || isCloudRoot(rootPath)) {
    const raw = await invoke<RawEditorPayload>("get_editor_payload");
    return normalizePayload(raw);
  }
  const raw = await invoke<RawEditorPayload>("set_active_tab", {
    rootPath,
    tab,
  });
  return normalizePayload(raw);
}

/**
 * Persist the last in-app route to `.zwlocal`. Desktop/Tauri only — no-op for
 * cloud/demo roots and non-Tauri (online editor trusts the URL).
 */
export async function setLastRoute(
  rootPath: string,
  route: string,
): Promise<void> {
  if (
    isTauri() === false ||
    rootPath === "" ||
    isCloudRoot(rootPath) ||
    isDemoRoot(rootPath)
  ) {
    return;
  }
  await invoke("set_last_route", { rootPath, route });
}

export async function readLastRoute(rootPath: string): Promise<string | null> {
  if (
    isTauri() === false ||
    rootPath === "" ||
    isCloudRoot(rootPath) ||
    isDemoRoot(rootPath)
  ) {
    return null;
  }
  return invoke<string | null>("read_last_route", { rootPath });
}

/** Clears the backend's tracked "active file" for this window — must be
 * called whenever the frontend blanks its own active file (Trash, or a
 * Notes/Database tab with nothing to resume) so the next real switch/open
 * command doesn't persist the frontend's blanked buffer over the file the
 * backend still thinks is open. See `clear_active_editor_buffer` (Rust). */
export async function clearActiveEditorBuffer(): Promise<void> {
  if (isDemoSessionActive()) {
    await invoke("clear_active_editor_buffer");
    return;
  }
  if (isTauri() === false) {
    await clearCloudEditorActiveBuffer();
    return;
  }
  await invoke("clear_active_editor_buffer");
}

export async function updateSceneSynopsis(
  rootPath: string,
  folder: string,
  sceneFile: string,
  synopsis: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("update_scene_synopsis", {
    rootPath,
    folder,
    sceneFile,
    synopsis,
  });
  return normalizePayload(raw);
}

export async function createProjectScene(
  rootPath: string,
  folder: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("create_project_scene", {
    rootPath,
    folder,
  });
  return normalizePayload(raw);
}

export async function renameProjectScene(
  rootPath: string,
  folder: string,
  fromSceneFile: string,
  toSceneFile: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("rename_project_scene", {
    rootPath,
    folder,
    fromSceneFile,
    toSceneFile,
  });
  return normalizePayload(raw);
}

export async function deleteProjectScene(
  rootPath: string,
  folder: string,
  sceneFile: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("delete_project_scene", {
    rootPath,
    folder,
    sceneFile,
  });
  return normalizePayload(raw);
}

export async function moveProjectScene(
  rootPath: string,
  fromFolder: string,
  sceneFile: string,
  toFolder: string,
  toSceneFile?: string | null,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("move_project_scene", {
    rootPath,
    fromFolder,
    sceneFile,
    toFolder,
    toSceneFile: toSceneFile ?? null,
  });
  return normalizePayload(raw);
}

export async function switchProjectChapter(
  rootPath: string,
  folder: string,
  sceneFile: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("switch_project_chapter", {
    rootPath,
    folder,
    sceneFile,
  });
  return normalizePayload(raw);
}
